import { ulid } from 'ulid';
import {
  ApiError,
  addAlbumItems,
  addMember,
  createAlbum,
  createFile,
  createFolder,
  createGroup,
  deleteAlbum,
  deleteFile,
  deleteFolder,
  deleteGroup,
  deleteReaction,
  getAlbum,
  getGroupFeed,
  getShares,
  shareAlbum,
  getFolders,
  getGroups,
  idOf,
  leaveGroup,
  listAlbumItems,
  listAlbums,
  listFiles,
  listMembers,
  listReactions,
  moderateReaction,
  patchAlbum,
  patchGroup,
  putReaction,
  removeAlbumItem,
  removeFromGroup,
  removeMember,
  completeAvatarUpload,
  entityOfFeedItem,
  putProfile,
  startAvatarUpload,
  type Entity,
  type FeedItem,
} from './api.js';
import { uploadFile, waitReady } from './upload.js';
import { EXACT_MAX_BYTES, knownHashes, planImport, sha256Hex } from './duplicates.js';
import { followDerivatives } from './derivatives.js';
import { formatSize } from './format.js';
import { sync } from './sync.js';
import { t } from './i18n.js';
import { completeFile, describeFile, getFile, getFileUrl, listVersions, patchFile } from './api.js';
import { listTrash, restoreAlbum, restoreFile } from './api.js';
import {
  createAdminUser,
  describeBacklog as describeBacklogRoute,
  visualHashBacklog as visualHashBacklogRoute,
  getAdminStats,
  listAdminUsers,
  resetAdminUserPassword,
  setAdminUserStatus,
  type AdminStats,
  type AdminUser,
} from './api.js';
import { authAgeSeconds, beginLogin, currentSession } from './auth.js';
import { forgetAvatar } from './avatars.js';
import { signal } from '@preact/signals';
import { afterSave, readNote, readRevision, revOf, saveNote, type SaveOutcome } from './notes.js';
import {
  forgetParcel,
  linkParcel,
  prepare,
  prepareThumb,
  readyFor,
  send,
  sendableOf,
  thumbFor,
  type Parcel,
} from './send.js';
import { linkableOf, linkUrl, type LinkTarget } from './link.js';
import { dismissOverlay, pushOverlay, pushView, replaceView } from './navigation.js';
import {
  ROOT,
  albumItems,
  albums,
  busy,
  clearPicked,
  detail,
  detailBusy,
  editor,
  openPicker,
  openViewer,
  patchEditor,
  reader,
  selection,
  currentFolder,
  edges,
  describing,
  duplicatesMode,
  entities,
  importPrompt,
  items,
  error,
  feed,
  feedBusy,
  feedCursor,
  folders,
  forgetEntity,
  form,
  groups,
  lastFolder,
  me,
  members,
  rememberLastAlbum,
  memberships,
  mergeEntities,
  parentOf,
  reactors,
  revealFolder,
  session,
  shares,
  trash,
  view,
  type ConflictState,
  type TrashItem,
  type View,
  uploadProgress,
} from './state.js';

/**
 * Les actions — **tout ce qui appelle l'API ou mute l'état, et rien qui dessine**.
 *
 * La séparation n'est pas décorative : un écran qui appelle directement une route
 * finit par en appeler deux fois, et une action qui touche au DOM ne peut pas être
 * réutilisée par l'écran suivant. Ici, `ui/` ne connaît que ces fonctions.
 */

/**
 * Enrobe une action : elle affiche son attente, capture son erreur, et libère
 * dans tous les cas. Une action qui échoue sans le dire est la seule façon dont
 * une interface ment à son utilisateur.
 */
export async function act(label: string, fn: () => Promise<void>): Promise<void> {
  busy.value = label;
  error.value = undefined;
  try {
    await fn();
  } catch (err) {
    error.value = messageOf(err);
  } finally {
    busy.value = undefined;
  }
}

const CODES: Record<string, string> = {
  // Les trois refus de l'avatar sont **corrigeables par l'utilisateur** : ils
  // méritent donc une phrase, là où un code brut le laisserait sans geste.
  format_rejected: 'error.avatarFormat',
  dimensions_unknown: 'error.avatarFormat',
  pixels_exceeded: 'error.avatarPixels',
  size_exceeded: 'error.tooLarge',
  folder_not_empty: 'error.folderNotEmpty',
  folder_root: 'error.folderRoot',
  folder_unknown: 'error.folderUnknown',
  folder_invalid: 'error.folderInvalid',
  folder_too_deep: 'error.folderTooDeep',
  folder_too_long: 'error.folderTooLong',
  folder_limit: 'error.folderLimit',
  name_required: 'error.nameRequired',
  quota_exceeded: 'error.quotaExceeded',
  // R6 : les bornes d'une saisie de mots-clés et de description.
  tag_too_long: 'error.tagTooLong',
  too_many_tags: 'error.tooManyTags',
  description_too_long: 'error.descriptionTooLong',
  describe_disabled: 'error.describeDisabled',
  not_ready: 'error.notReady',
};

export function messageOf(err: unknown): string {
  if (err instanceof ApiError) {
    // Les codes que l'utilisateur peut corriger lui-même méritent une phrase ;
    // les autres gardent leur code, qui est ce qu'on lui demandera de recopier.
    const key = CODES[err.code];
    return key ? t(key) : t('error.generic', { status: err.status, code: err.code });
  }
  return err instanceof Error ? err.message : String(err);
}

// ─────────────────────────────────────────────────────────────────────────────
// Chargements
// ─────────────────────────────────────────────────────────────────────────────

/**
 * L'arborescence vient de `GET /folders`, **pas du miroir**, et c'est délibéré :
 * `itemCount` est maintenu par la transaction qui déplace ou supprime une entité,
 * et le recalculer localement donnerait un chiffre juste seulement si le miroir
 * est complet. Un compteur qu'on affiche doit venir de là où il est écrit.
 */
export async function refreshFolders(): Promise<void> {
  const res = await getFolders();
  folders.value = res.items;
  await session.cache?.putFolders(res.items);

  // Le dossier courant a pu disparaître — supprimé ici, ou sur un autre appareil.
  const v = view.value;
  if (v.kind === 'folder' && !res.items.some((f) => f.folder === v.folder)) {
    view.value = { kind: 'folder', folder: ROOT };
    // **Remplacer et non empiler** : un retour ne doit pas ramener dans un
    // dossier qui n'existe plus, il n'y aurait rien à y montrer.
    replaceView(view.value);
  }
}

/** Le premier remplissage, quand aucun miroir n'a pu servir. */
export async function loadFromApi(): Promise<void> {
  const [own, media] = await Promise.all([
    listFiles({ folder: ROOT, limit: 200 }),
    listFiles({ type: 'MEDIA', limit: 200 }),
  ]);
  mergeEntities([...own.items, ...media.items]);
}

/** Drainage de `/changes`, puis reprojection. Sans miroir, il n'y a rien à drainer. */
export async function pullChanges(): Promise<void> {
  if (!session.cache) return;
  await sync(session.cache, session.cursor, {
    // **Le drainage se compte** dès qu'il dépasse une page : un premier plein de
    // vingt mille éléments dure, et « Synchronisation… » immobile pendant une
    // minute se lit comme un blocage. Le libellé ne remplace que celui d'une
    // action en cours — jamais ne fait apparaître un bandeau qui n'y était pas.
    onProgress: ({ pages, items }) => {
      if (pages > 1 && busy.value !== undefined) {
        busy.value = t('app.syncingCount', { count: items });
      }
    },
  });
  // Relu du miroir plutôt que rendu par `sync` : c'est la transaction qui fait
  // foi, et le curseur en mémoire doit suivre celui qu'elle a écrit — sinon le
  // drainage suivant reprendrait des pages déjà appliquées.
  const mirror = await session.cache.read();
  entities.value = mirror.entities;
  // Arêtes et appartenances **depuis 4.6** : elles étaient drainées puis jetées,
  // et le curseur avançait quand même (D-23). Elles peuplent les partages.
  edges.value = mirror.edges;
  memberships.value = mirror.memberships;
  session.cursor = mirror.cursor;
  await refreshFolders();
}

/**
 * **Aller chercher ce que les autres ont fait** *(2026-08-16)*.
 *
 * Ce n'est pas un bouton de confort : jusqu'ici `pullChanges` n'avait que **deux**
 * appelants — le démarrage de l'application et la sortie d'un groupe. Un onglet
 * resté ouvert ne voyait donc jamais une photo versée par quelqu'un d'autre, ni
 * une note modifiée ailleurs. Le retour d'usage le dit sans détour : « il n'y a
 * pas de bouton permettant le refresh pour récupérer des modifications en ligne ».
 *
 * Il draine `/changes`, et **rien d'autre par lui-même** : le miroir est la source
 * de tout ce qui se peint. Les deux listes qui ne passent pas par lui sont
 * relues ici — les partages, dont la projection (`ItemCount`, `ContribPolicy`)
 * arrive par `GET /albums`, et les groupes, dont l'effectif ne voyage pas non
 * plus. Sans elles, actualiser aurait rafraîchi les objets et laissé leurs
 * contenants en arrière, ce qui est le genre d'incohérence qu'on met une heure à
 * comprendre.
 */
export function onRefresh(): Promise<void> {
  return act(t('header.refreshing'), async () => {
    await pullChanges();
    // Forcé : `loadAlbums()` sans argument sort tout de suite si la liste est
    // déjà chargée, ce qui est exactement le cas ici.
    await loadAlbums(true);
    groups.value = undefined;
    await loadGroups();
  });
}

/**
 * Les groupes, **à la demande et une seule fois**.
 *
 * `GET /me` porte déjà l'appartenance ; cette route ajoute l'effectif et la date
 * de création, qui ne valent pas un appel tant que personne ne regarde l'écran
 * *Partagé*. `next` y vaut toujours `null` (D29 + I-30) : rien à dérouler.
 */
export async function loadGroups(): Promise<void> {
  if (groups.value !== undefined) return;
  const page = await getGroups();
  groups.value = page.items;
}

/**
 * Les membres d'un groupe, **par groupe et une seule fois**.
 *
 * Ils ne sont pas dans le miroir, et ils n'ont pas à y être : `/changes` livre nos
 * appartenances à nous, jamais celles des autres. C'est une lecture d'écran.
 */
export async function loadMembers(gid: string, force = false): Promise<void> {
  if (members.value[gid] !== undefined && !force) return;
  const page = await listMembers(gid);
  members.value = { ...members.value, [gid]: page.items };
}

/**
 * Le fil d'un groupe. **Servi par la route, pas par le miroir**, et la distinction
 * est un choix : le miroir porte bien les arêtes depuis que D-23 est fermée, mais
 * la route les rend **ordonnées, paginées et nommées** — `ownerAlias` y est
 * calculé, là où l'arête ne porte qu'un `Owner` opaque. Le miroir sert la pastille
 * de nouveautés, qui ne demande qu'un compte ; le fil sert l'écran, qui demande
 * des noms.
 */
export function loadFeed(gid: string, more = false): Promise<void> {
  if (feedBusy.value) return Promise.resolve();
  if (more && feedCursor.value === null) return Promise.resolve();

  feedBusy.value = true;
  return getGroupFeed(gid, more ? (feedCursor.value ?? undefined) : undefined)
    .then((page) => {
      // Le fil monte du plus récent au plus ancien à l'écran, alors que la route
      // rend l'ordre de fraîcheur croissant : le tri est fait ici, une fois.
      const merged = more ? [...feed.value, ...page.items] : page.items;
      feed.value = [...merged].sort((a, b) =>
        String(b.EdgeUpdatedAt ?? '').localeCompare(String(a.EdgeUpdatedAt ?? '')),
      );
      // Une page plus courte que demandé est la fin : insister rendrait la même.
      feedCursor.value = page.items.length === 0 ? null : page.cursor;
    })
    .catch((err) => {
      error.value = messageOf(err);
    })
    .finally(() => {
      feedBusy.value = false;
    });
}

/**
 * Naviguer. **Empile une entrée d'historique**, ce qui est ce qui donne au geste
 * de retour — bouton du site ou geste du système — quelque chose où revenir.
 *
 * Le travail lui-même vit dans `applyView` : le retour doit pouvoir rejouer une
 * vue *sans* réempiler l'entrée d'où il vient, sans quoi la pile grandirait à
 * chaque pression et on ne sortirait jamais.
 */
export function openView(next: View): void {
  applyView(next);
  pushView(next);
}

export function applyView(next: View): void {
  const previous = view.value;
  view.value = next;
  form.value = undefined;

  if (next.kind === 'group') {
    // Le fil est **remis à zéro en changeant de groupe**, jamais en changeant
    // d'onglet : le garder ferait apparaître le contenu d'un cercle dans un autre
    // le temps d'un aller-retour, ce qui est la pire seconde possible.
    if (previous.kind !== 'group' || previous.gid !== next.gid) {
      feed.value = [];
      feedCursor.value = null;
    }
    void loadMembers(next.gid).catch((err) => (error.value = messageOf(err)));
    void loadAlbums().catch((err) => (error.value = messageOf(err)));
    if (next.tab === 'feed' && feed.value.length === 0) void loadFeed(next.gid);
    return;
  }

  // Ouvrir `/a/b/c` en laissant `/a` replié ferait disparaître de l'arbre le
  // dossier qu'on vient d'ouvrir : le titre dirait une chose, l'arborescence une
  // autre.
  if (next.kind === 'folder') {
    revealFolder(next.folder);
    // **Où l'on était**, pour que le commutateur d'axe y ramène. Sans cette
    // mémoire, passer par *Tout* et revenir rejetterait à la racine — un
    // aller-retour entre deux façons de regarder ferait perdre sa place.
    lastFolder.value = next.folder;
  }
  // Une collection ne vient **pas** du miroir : son contenu est porté par des
  // arêtes, et le filtrer donnerait un album toujours vide. Elle se recharge donc
  // à chaque arrivée — y compris quand l'arrivée est un retour.
  if (next.kind === 'collection') {
    void loadAlbumItems(next.aid);
    void loadAlbums().catch((err) => (error.value = messageOf(err)));
    return;
  }

  // **Le filtre par partage a besoin des noms** *(R3, 2026-09-12)*. Les entités du
  // miroir portent leurs `aid` depuis l'étape 1 ; ce que `GET /albums` apporte est
  // le **nom** de chacun, et un filtre qui proposerait des ULID ne serait pas un
  // filtre. L'appel est celui de toutes les autres destinations — non forcé, donc
  // une fois par session — et il ne bloque pas : sans lui, le champ n'apparaît
  // simplement pas encore.
  if (next.kind === 'folder' || next.kind === 'files') {
    void loadAlbums().catch((err) => (error.value = messageOf(err)));
  }

  // Aucun appel pour l'espace ni la galerie : la vue est une projection du
  // miroir, c'est tout l'intérêt. Les groupes, eux, ne sont pas dans le miroir —
  // `membership` est écarté du drainage (D-23) — et se chargent donc à la
  // demande, **sans attente bloquante** : la liste apparaît quand elle arrive.
  if (next.kind === 'shared') {
    void loadGroups().catch((err) => (error.value = messageOf(err)));
    void loadAlbums().catch((err) => (error.value = messageOf(err)));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Intègre au miroir des entités **rendues par une écriture**.
 *
 * Ce chemin existe parce que `/changes` est servi par un index secondaire dont la
 * propagation n'a aucune borne documentée : attendre que la synchronisation
 * ramène ce qu'on vient de créer ferait clignoter l'écran, ou pire, laisserait
 * croire à un échec. La réponse d'écriture fait autorité pour cet item-là, et
 * pour lui seul — le curseur, lui, ne bouge pas.
 */
async function adopt(fresh: readonly Entity[]): Promise<void> {
  mergeEntities(fresh);
  await session.cache?.put(fresh.map((e) => ({ key: idOf(e), value: e })));
}

export function onNewFolder(path: string): Promise<void> {
  return act(t('space.creatingFolder'), async () => {
    const created = await createFolder(path);
    await refreshFolders();
    form.value = undefined;
    view.value = { kind: 'folder', folder: created.folder };
    pushView(view.value);
    // Créer `/a/b` alors que `/a` est replié le rendrait invisible aussitôt né.
    revealFolder(created.folder);
  });
}

export async function onNewNote(name: string): Promise<void> {
  const folder = currentFolder();
  let born: Entity | undefined;

  await act(t('space.creatingNote'), async () => {
    // Le `fid` vient du client (D14) : l'entité a une identité avant même d'avoir
    // atteint le serveur, et un rejeu ne crée pas de doublon.
    const created = await createFile({ fid: ulid(), type: 'NOTE', name, folder });
    form.value = undefined;
    await adopt([created]);
    await refreshFolders();
    born = created;
  });

  // **Nommer une note, c'est vouloir l'écrire** *(N4, 2026-08-18)*. Le geste
  // s'arrêtait sur une tuile vide au milieu de la grille, et il fallait la
  // retrouver puis l'ouvrir pour taper la première ligne — deux clics de plus
  // pour la seule suite que le formulaire pouvait avoir.
  //
  // **Hors du `act`, et non dedans** : deux `act` imbriqués se marchent dessus,
  // le plus intérieur rendant `busy` à `undefined` alors que l'extérieur
  // travaille encore. `born` reste `undefined` si la création a échoué — l'erreur
  // est déjà à l'écran, et ouvrir un éditeur sur une note qui n'existe pas la
  // recouvrirait.
  if (born) await onEdit(born);
}

export function onDeleteFolder(path: string): Promise<void> {
  return act(t('space.deletingFolder'), async () => {
    await deleteFolder(path);
    view.value = { kind: 'folder', folder: ROOT };
    // Le dossier qu'on regardait n'existe plus : l'entrée qui le décrivait est
    // remplacée, pas doublée — un retour y mènerait dans le vide.
    replaceView(view.value);
    await refreshFolders();
  });
}

/**
 * **Écarter les doublons exacts avant d'envoyer** *(K2, `doublons.md` §3)*.
 *
 * Trois temps : hacher chaque fichier, comparer aux empreintes de l'espace — le
 * miroir, la corbeille, la sélection elle-même —, et s'il y a des doublons,
 * **demander** (DB2). Rend les fichiers à envoyer avec leur empreinte, ou
 * `undefined` si le hachage a échoué (l'erreur est déjà à l'écran).
 *
 * **La corbeille se lit ici**, parce que le miroir ne la garde pas : une photo
 * supprimée puis réimportée est un doublon qu'on peut restaurer (DB8).
 */
async function withoutDuplicates(
  files: readonly File[],
): Promise<{ file: File; sha256?: string }[] | undefined> {
  let plan: ReturnType<typeof planImport> | undefined;
  await act(t('space.checkingDuplicates', { index: 0, total: files.length }), async () => {
    const hashes: (string | undefined)[] = [];
    for (let i = 0; i < files.length; i++) {
      busy.value = t('space.checkingDuplicates', { index: i + 1, total: files.length });
      const file = files[i]!;
      // Au-delà de la borne, pas d'empreinte : le fichier part comme avant K1 (DB9).
      hashes.push(file.size <= EXACT_MAX_BYTES ? await sha256Hex(file) : undefined);
    }
    const trashed: Entity[] = [];
    let after: string | undefined;
    do {
      const page = await listTrash(200, after);
      trashed.push(...page.items);
      after = page.next ?? undefined;
    } while (after);
    plan = planImport(hashes, knownHashes(entities.value, trashed));
  });
  if (!plan) return undefined;

  const { hashes, duplicateOf } = plan;
  const items = files.map((file, i) => ({ file, sha256: hashes[i], duplicateOf: duplicateOf[i] }));
  const duplicates = items.filter((it) => it.duplicateOf !== undefined);
  if (duplicates.length === 0) return items;

  const force = await new Promise<boolean>((resolve) => {
    importPrompt.value = {
      duplicates: duplicates.length,
      inTrash: duplicates.filter((it) => it.duplicateOf === 'trash').length,
      choose: (answer) => {
        importPrompt.value = undefined;
        resolve(answer);
      },
    };
  });
  return force ? items : items.filter((it) => it.duplicateOf === undefined);
}

/**
 * Envoie une sélection de fichiers, **un par un**.
 *
 * En série et non en parallèle, délibérément : chaque envoi est un `PUT` direct
 * vers S3 dont la taille n'est bornée que par le type (2 Gio pour un média), et
 * dix en vol saturent la liaison montante sans rien accélérer. La progression est
 * affichée fichier par fichier, ce qui est aussi la seule forme d'attente honnête.
 */
export async function onUpload(selection: readonly File[]): Promise<void> {
  const folder = currentFolder();
  const chosen = await withoutDuplicates(selection);
  if (!chosen || chosen.length === 0) return;
  const files = chosen.map((c) => c.file);
  return act(t('space.uploading'), async () => {
    const created: Entity[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const where = { name: files[i]!.name, index: i + 1, total: files.length };
        busy.value = t('space.uploadingOne', where);
        // **Les octets du fichier courant**, remis à zéro à chaque entrée : un
        // reste de la photo précédente ferait démarrer la barre à 100 %.
        uploadProgress.value = { sent: 0, total: files[i]!.size };

        created.push(
          await uploadFile(files[i]!, folder, {
            sha256: chosen[i]!.sha256,
            // **Une reprise se dit.** Elle renvoie les octets depuis le début sur
            // une URL neuve : sur une vidéo, c'est une barre d'attente qui repart
            // à zéro sans explication, et l'utilisateur conclut au blocage.
            onPhase: (phase) => {
              busy.value = t(
                phase === 'resuming' ? 'space.resumingOne' : 'space.uploadingOne',
                where,
              );
            },
            // **Le texte ne bouge qu'au point de pourcentage**, la barre à chaque
            // événement. Réécrire la phrase cent fois par seconde ferait
            // clignoter une ligne que personne ne peut lire à cette vitesse,
            // alors qu'un `<progress>` est fait pour avancer en continu.
            onProgress: (sent, total) => {
              const avant = uploadProgress.value;
              uploadProgress.value = { sent, total };
              if (total <= 0) return;
              const pas = Math.floor((sent / total) * 100);
              const precedent = avant && avant.total > 0
                ? Math.floor((avant.sent / avant.total) * 100)
                : -1;
              if (pas !== precedent) {
                busy.value = t('space.uploadingBytes', {
                  ...where,
                  sent: formatSize(sent),
                  size: formatSize(total),
                });
              }
            },
          }),
        );
      }
    } finally {
      // **Dans un `finally`** : un envoi qui échoue au troisième fichier laissait
      // sinon une barre figée derrière le message d'erreur, ce qui se lit comme un
      // envoi encore en cours.
      uploadProgress.value = undefined;
    }

    // **On adopte tout de suite, dans l'état du moment, et on rend la main.**
    //
    // La ligne d'avant attendait `Promise.all(media.map(waitReady))`, ce qui
    // cumulait deux défauts *(corrigés le 2026-09-12)* : **56 sondages concurrents
    // toutes les 2 s ≈ 28 req/s** contre un stage étranglé à 20 — d'où le `429`
    // rapporté —, et une attente qui tenait `busy`, lequel **désactive tous les
    // boutons du site**. L'écran affichait « traitement en cours » et refusait tout
    // geste pendant que les dérivés se construisaient.
    //
    // Les tuiles paraissent donc en « traitement en cours », ce qui est **exact** :
    // le worker travaille déjà, il a été enfilé à chaque `complete`, et il traite en
    // parallèle. C'est le suiveur qui les remplace une par une, à débit borné et
    // sans rien bloquer.
    await adopt(created);
    await refreshFolders();

    const media = created
      .filter((e) => e.Type === 'MEDIA' && e.Fid && e.State !== 'READY')
      .map((e) => e.Fid!);
    // **Détaché volontairement** : attendre ici réintroduirait exactement ce qu'on
    // vient de retirer. Le suiveur publie son reste-à-faire dans `processing`, que
    // l'écran montre sans immobiliser personne.
    if (media.length > 0) {
      void followDerivatives(media, { adopt: (fresh) => adoptFresh(fresh.Fid!, fresh) }).then(() =>
        me.value?.features?.describe ? pullDescriptionsAfterUpload() : undefined,
      );
    }
  });
}

/**
 * **Retenter les dérivés d'une entité en échec** *(2026-09-12)*.
 *
 * Le geste manquait, et son absence a coûté 42 photos : un `FAILED` était un
 * cul-de-sac à l'écran alors qu'il ne l'était **pas** dans le socle. `POST
 * /files/{fid}/complete` accepte une entité `FAILED` depuis l'étape 1 — elle ne
 * refuse que `PROCESSING` et `READY` —, les octets de la source sont déjà dans le
 * stockage, le serveur les reconstate par `HeadObject` et réenfile le job.
 *
 * **Aucune route neuve, donc, et aucun octet à renvoyer.** C'est ce qui rend la
 * reprise praticable sur une photo de 38 Mio derrière une liaison lente.
 *
 * Trois choses à ne pas rater, et chacune a déjà mordu ailleurs :
 *
 * ① **La réponse fait autorité, et on l'adopte entière.** Le voisinage de ce
 *    fichier avertit de l'inverse — `adoptReaction` n'emprunte que `reactions`
 *    parce qu'écraser l'entité retirerait `Folder` d'un objet **reçu**. Ici le
 *    danger n'existe pas : la route exige d'être propriétaire, donc le
 *    sérialiseur rend aussi les champs de propriétaire. Recomposer à la main
 *    serait au contraire le piège de N5 — un champ oublié, invisible depuis
 *    l'écran où l'on agit.
 * ② **La fiche et le miroir bougent tous les deux.** La fiche est ce qu'on
 *    regarde en appuyant ; le miroir est ce qui dessine la tuile derrière.
 * ③ **On attend l'issue.** Le job est asynchrone : rendre la main sur
 *    `PROCESSING` laisserait un sablier que rien ne viendrait rafraîchir, et
 *    l'utilisateur conclurait à un second échec. `waitReady` rend `READY` **ou**
 *    `FAILED` — et un second `FAILED` est une réponse, pas une panne : il porte
 *    cette fois un motif que l'écran sait nommer.
 */
export function onRetryDerivatives(fid: string): Promise<void> {
  return act(t('entity.retrying'), async () => {
    const relaunched = await completeFile(fid, ['source']);
    adoptFresh(fid, relaunched);

    // L'entité **d'après** le worker : c'est elle qui porte le `Variants.thumb.size`
    // sans lequel la tuile resterait un sablier, et le motif si l'échec se répète.
    const settled = await waitReady(fid);
    adoptFresh(fid, settled);
  });
}

/** La fiche ouverte **et** le miroir, d'une seule réponse d'autorité. */
function adoptFresh(fid: string, fresh: Entity): void {
  const current = detail.value;
  if (current && idOf(current) === fid) detail.value = fresh;
  void adopt([fresh]);
}

/**
 * **Envoyer une photo depuis l'éditeur de notes** *(N2, 2026-08-17)*, et rendre
 * son `fid` — c'est lui que la note écrira.
 *
 * Trois différences avec `onUpload`, et chacune est une décision :
 *
 * ① **Aucun dossier n'est passé**, même si l'on écrit une note rangée quelque
 *    part. Le serveur pose alors le dossier par défaut du type — `/Photos` —, ce
 *    qui est la seule place juste : la photo n'appartient pas à `/Notes` parce
 *    qu'une note la montre, et elle doit se retrouver dans la photothèque comme
 *    n'importe quelle autre.
 * ② **On n'attend pas le dérivé.** `waitReady` bloquerait l'insertion jusqu'à
 *    quatre-vingt-dix secondes sur une photo lourde ; la note reçoit le `fid` tout
 *    de suite, et c'est la vue de l'image qui patiente (`noteImageUrl`).
 * ③ **Un seul fichier.** Le geste est « illustrer cet endroit du texte » ; en
 *    verser dix ferait dix images là où le curseur en attendait une.
 */
export async function onUploadNoteImage(file: File): Promise<string | undefined> {
  let fid: string | undefined;
  await act(t('space.uploadingOne', { name: file.name, index: 1, total: 1 }), async () => {
    const created = await uploadFile(file, undefined, {
      onPhase: (phase) => {
        busy.value = t(
          phase === 'resuming' ? 'space.resumingOne' : 'space.uploadingOne',
          { name: file.name, index: 1, total: 1 },
        );
      },
    });
    await adopt([created]);
    fid = created.Fid;
  });
  return fid;
}

/**
 * **La corbeille** — chargée à l'ouverture de l'écran, et jamais avant.
 *
 * Elle ne se déduit d'aucune liste déjà en mémoire : un objet supprimé a quitté
 * les index de listing, il n'est donc dans le miroir local d'aucun appareil. Un
 * appel, une fois, quand l'écran s'ouvre.
 */
export function loadTrash(): Promise<void> {
  return act(t('trash.loading'), async () => {
    trash.value = (await listTrash()).items;
  });
}

/**
 * **Restaurer** — le seul geste de cet écran, et il fait plus qu'il n'en a l'air.
 *
 * Le serveur relève l'objet **et les accès que la suppression avait couchés** :
 * un partage restauré retrouve son audience, une photo retrouve ses albums. Le
 * client, lui, n'a rien à reconstruire — il **draine `/changes`**, qui livre
 * l'objet redevenu vivant comme il avait livré sa disparition. Reconstituer
 * l'état à la main depuis la réponse produirait un miroir plausible et faux : ni
 * les arêtes relevées ni les compteurs recalculés n'y sont.
 *
 * Un partage revient avec `shareState: RESTORING` : son **contenu** arrive
 * derrière, par une cascade paginée. L'écran le dit plutôt que de laisser
 * quelqu'un conclure que le partage est revenu vide.
 */
export function onRestoreFromTrash(item: TrashItem): Promise<void> {
  return act(t('trash.restoring'), async () => {
    if (item.Aid) await restoreAlbum(item.Aid);
    else await restoreFile(item.Fid!);

    // Retiré de la liste **après** le succès, jamais avant : un retrait optimiste
    // ferait disparaître de la corbeille un objet qu'un `404 purged` vient de
    // refuser de rendre — donc le seul écran depuis lequel on pouvait le voir.
    trash.value = (trash.value ?? []).filter((i) => idOf(i) !== idOf(item));

    // Un partage restauré ne revient pas seul : son audience est rétablie
    // synchroniquement, son contenu par une cascade. `GET /albums` porte la
    // première, `/changes` le second.
    if (item.Aid) await loadAlbums(true);
    await pullChanges();
  });
}

export function onDeleteEntity(entity: Entity): Promise<void> {
  return act(t('space.deleting'), async () => {
    // Un album se supprime par sa propre route ; à cette étape l'UI n'en crée
    // pas, et refuser explicitement vaut mieux qu'appeler la mauvaise.
    if (!entity.Fid) throw new Error(t('error.albumDeleteLater'));
    await deleteFile(entity.Fid);
    forgetEntity(entity.Fid);
    await session.cache?.put([{ key: entity.Fid, value: null }]);
    await refreshFolders();
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Ouvrir, inspecter, ranger
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sélectionne une entité et charge sa **fiche complète**.
 *
 * `GET /files/{fid}` et non l'entité du miroir : les listings et `/changes` ne
 * portent pas tout — la description, les tags et les réactions n'y sont pas —,
 * et l'inspecteur ment s'il affiche l'un en croyant montrer l'autre.
 */
export async function select(fid: string | undefined): Promise<void> {
  selection.value = fid;
  detail.value = undefined;
  if (!fid) return;

  // La fiche **couvre** sur un téléphone : elle mérite donc son entrée
  // d'historique, comme toute surcouche, et le geste de retour la referme.
  pushOverlay('inspector');
  detailBusy.value = true;
  try {
    const full = await getFile(fid);
    // La sélection a pu changer pendant l'aller-retour : poser une fiche
    // périmée sur une autre sélection est le défaut le plus déroutant qui soit.
    if (selection.value === fid) detail.value = full;
  } catch (err) {
    if (selection.value === fid) error.value = messageOf(err);
  } finally {
    detailBusy.value = false;
  }
}

/**
 * Refermer la fiche. **Par l'historique** quand c'est elle qui y est inscrite —
 * la croix et le geste de retour doivent faire la même chose, sinon l'un des deux
 * laisse dans la pile une entrée qui décrit un écran qu'on ne voit plus.
 */
export function closeInspector(): void {
  dismissOverlay('inspector', () => {
    selection.value = undefined;
    detail.value = undefined;
  });
}

/**
 * Ouvrir. **Le type décide de la surface**, pas l'écran d'où l'on vient :
 * une photo se feuillette, un document se lit, un album se parcourt.
 *
 * ### Ouvrir n'est pas inspecter *(2026-08-14)*
 *
 * Cette fonction commençait par `select()`, c'est-à-dire par ouvrir la **fiche**
 * de l'objet en plus de l'objet. Sur un grand écran, la colonne de droite se
 * remplissait — inoffensif, et même commode. Sur un téléphone, la fiche est une
 * feuille modale : toucher une photo ouvrait donc la visionneuse *et* posait la
 * fiche par-dessus, si bien qu'on voyait les détails et non la photo qu'on venait
 * de demander. C'est exactement le retour d'usage.
 *
 * La règle est celle du menu d'objet, prise à la lettre : **la fiche s'ouvre par
 * « … → Détails », et par rien d'autre**. Le menu est désormais atteignable
 * depuis la visionneuse et depuis le lecteur (`viewer.tsx`, `reader.tsx`), ce qui
 * enlève à ce raccourci sa dernière justification.
 */
export function onOpen(entity: Entity, list: readonly Entity[] = []): void {
  // Un album **se parcourt** : il ouvre sa propre grille. C'est la seule entité
  // dont le contenu ne vient pas du miroir — l'appartenance est portée par des
  // arêtes, et filtrer le miroir donnerait un album systématiquement vide.
  if (entity.Aid) {
    void onOpenAlbum(entity.Aid);
    return;
  }

  if (entity.Type === 'MEDIA') {
    const media = list.filter((e) => e.Type === 'MEDIA' && e.Fid);
    const index = media.findIndex((e) => e.Fid === entity.Fid);
    openViewer(media.length > 0 ? media : [entity], index >= 0 ? index : 0);
    pushOverlay('viewer');
    return;
  }
  if (entity.Type === 'NOTE' || entity.Type === 'DOC') {
    reader.value = entity;
    pushOverlay('reader');
  }
}

/**
 * Ouvrir depuis le fil. **Un retrait ne s'ouvre pas** : la ligne subsiste pour
 * dire que l'accès a été coupé — c'est ce qu'un membre vient y lire —, mais il
 * n'y a plus d'octets derrière, et proposer le geste pour le voir échouer serait
 * pire que de ne pas le proposer.
 */
export function onOpenFeedItem(item: FeedItem, list: readonly FeedItem[]): void {
  if (item.Deleted) return;
  onOpen(
    entityOfFeedItem(item),
    list.filter((i) => !i.Deleted).map(entityOfFeedItem),
  );
}

/**
 * **Ranger dans une collection**, depuis n'importe où — la tuile, la ligne, la
 * fiche, la barre de sélection. Le sélecteur couvre l'écran : il a donc son
 * entrée d'historique, comme les autres surcouches.
 */
export function onPickCollection(fids: readonly string[]): void {
  openPicker(fids);
  pushOverlay('picker');
}

export function onRename(entity: Entity, name: string): Promise<void> {
  const fid = entity.Fid;
  if (!fid) return Promise.resolve();
  return act(t('inspector.renaming'), async () => {
    const updated = await patchFile(fid, { name });
    mergeEntities([updated]);
    await session.cache?.put([{ key: fid, value: updated }]);
    detail.value = { ...(detail.value ?? updated), ...updated };
  });
}

/**
 * **Choisir l'affiche d'une vidéo** (M6).
 *
 * Le geste est le moins cher du produit : la pellicule est déjà à l'écran, la
 * case choisie en est une fenêtre, et l'écriture est **un entier**. Rien n'est
 * téléchargé, rien n'est produit, la source ne se réveille pas de Glacier.
 *
 * Il passe par le serveur plutôt que par un réglage local, et c'est tout l'objet
 * de `PosterFrame` : l'affiche est **projetée sur les arêtes** (E3), donc le fil
 * d'un groupe montre la case que le propriétaire a choisie. Un choix gardé dans le
 * navigateur n'aurait tenu ni jusqu'au téléphone, ni jusqu'aux autres.
 */
export function onPickPoster(entity: Entity, frame: number | null): Promise<void> {
  const fid = entity.Fid;
  if (!fid) return Promise.resolve();
  return act(t('poster.saving'), async () => {
    const updated = await patchFile(fid, { posterFrame: frame });
    mergeEntities([updated]);
    await session.cache?.put([{ key: fid, value: updated }]);
    detail.value = { ...(detail.value ?? updated), ...updated };
  });
}

/**
 * **Corriger la description ou les mots-clés** *(R6, 2026-09-13)*.
 *
 * Le serveur retire le champ écrit de `Described.Machine` : une correction n'est
 * plus jamais écrasée par un rejeu ni par un rattrapage — seul « Régénérer » le
 * peut, après confirmation. La réponse fait foi, comme pour tout `PATCH` : c'est
 * elle qui porte le marqueur à jour, et la mention « décrit automatiquement »
 * disparaît de la fiche sur cette seule réponse.
 */
export function onSaveDescribed(
  entity: Entity,
  patch: { readonly description?: string | null; readonly tags?: readonly string[] },
): Promise<void> {
  const fid = entity.Fid;
  if (!fid) return Promise.resolve();
  return act(t('inspector.saving'), async () => {
    const updated = await patchFile(fid, patch);
    mergeEntities([updated]);
    await session.cache?.put([{ key: fid, value: updated }]);
    detail.value = { ...(detail.value ?? updated), ...updated };
  });
}

/**
 * **« Régénérer »** *(R6)* — la confirmation, s'il y a une correction à perdre, est
 * prise par la fiche **avant** d'arriver ici (Q3).
 *
 * Le résultat **revient par la synchronisation**, pas par une route de suivi :
 * la description est une mutation de l'entité comme une autre, et `/changes` la
 * livre. Ce qui est propre à ce geste est seulement de **tirer** la synchronisation
 * quelques fois — le site n'en a pas de périodique, et une fiche qui ne bouge pas
 * après « Régénérer » se lirait comme un bouton cassé.
 */
export function onRegenerate(entity: Entity): Promise<void> {
  const fid = entity.Fid;
  if (!fid) return Promise.resolve();
  return act(t('describe.requesting'), async () => {
    await describeFile(fid);
    describing.value = new Set([...describing.value, fid]);
    void followDescription(fid, entity.Described?.At);
  });
}

/**
 * Les instants où l'on retire `/changes` après une demande de description. Une
 * photo seule est décrite en deux à cinq secondes ; sous un rattrapage, la file
 * cadencée à ~18 par minute peut la faire attendre. Quatre tirages sur une minute
 * et demie, puis on s'arrête : au-delà, le bouton d'actualisation reste le geste.
 */
const DESCRIPTION_PULLS_MS = [4_000, 10_000, 30_000, 90_000];

/** Tire la synchronisation jusqu'à ce que la description de `fid` ait changé. */
export async function followDescription(fid: string, before: string | undefined): Promise<void> {
  let elapsed = 0;
  try {
    for (const at of DESCRIPTION_PULLS_MS) {
      await new Promise((r) => setTimeout(r, at - elapsed));
      elapsed = at;
      try {
        await pullChanges();
      } catch {
        continue;
      }
      const fresh = entities.value.find((e) => e.Fid === fid);
      if (!fresh || fresh.Described?.At === before) continue;
      const open = detail.value;
      if (open && open.Fid === fid) detail.value = { ...open, ...fresh };
      return;
    }
  } finally {
    const next = new Set(describing.value);
    next.delete(fid);
    describing.value = next;
  }
}

/**
 * **Après un envoi, les descriptions arrivent derrière les vignettes** *(R5)*.
 * Elles sont enfilées au passage à `READY`, et la recherche (R7) lit le miroir :
 * sans un tirage de `/changes`, une photo qu'on vient d'envoyer serait introuvable
 * jusqu'au prochain rafraîchissement. Trois tirages, espacés — pas un sondage.
 */
export async function pullDescriptionsAfterUpload(): Promise<void> {
  for (const wait of [15_000, 45_000, 120_000]) {
    await new Promise((r) => setTimeout(r, wait));
    await pullChanges().catch(() => undefined);
  }
}

/**
 * Déplacer. Deux compteurs de dossier changent côté serveur, dans la même
 * transaction : c'est pourquoi l'arborescence est relue derrière plutôt que
 * décrémentée à la main.
 */
export function onMove(entity: Entity, folder: string): Promise<void> {
  const fid = entity.Fid;
  if (!fid) return Promise.resolve();
  return act(t('inspector.moving'), async () => {
    const updated = await patchFile(fid, { folder });
    mergeEntities([updated]);
    await session.cache?.put([{ key: fid, value: updated }]);
    detail.value = { ...(detail.value ?? updated), ...updated };
    await refreshFolders();
  });
}

/**
 * **Envoyer vers une autre application** *(N6, 2026-08-18)*.
 *
 * Le corps de la décision est dans [`send.ts`](send.ts) ; ce qui est ici, c'est
 * l'enchaînement, et il porte **le seul détail qui décide de la réussite** :
 * quand un colis est déjà prêt, `navigator.share()` est appelé **sans un seul
 * `await`** avant lui. L'activation utilisateur est consommée et courte — la
 * spec l'exige puis l'épuise —, et c'est ce chemin sans attente qui fait
 * aboutir le second clic quand le premier a manqué la fenêtre.
 *
 * Ni « envoyé » ni « annulé » ne s'écrivent à l'écran : **la feuille du système
 * est le retour**, et un bandeau après une annulation volontaire ferait croire à
 * une panne.
 */
export async function onSend(entity: Entity): Promise<void> {
  const what = sendableOf(entity);
  if (!what) return;

  const ready = readyFor(entity.Fid ?? '');
  // **Aucun `await` sur ce chemin.** Le retirer — même pour un `await` qui
  // résout aussitôt — rendrait l'activation caduque sur Safari iOS.
  if (ready) return handOver(ready);

  busy.value = t('send.preparing');
  error.value = undefined;
  try {
    return await handOver(await prepare(entity, what));
  } finally {
    busy.value = undefined;
  }
}

async function handOver(parcel: Parcel): Promise<void> {
  // **Chaque refus dit lequel il est.** La première version les repliait tous
  // sur « L'envoi a échoué » : quand photo et vidéo ont échoué le 2026-08-18, ce
  // message ne disait pas si les octets manquaient, si le navigateur refusait le
  // fichier, ou si le partage lui-même avait rendu la main.
  if (!parcel.ok) {
    error.value =
      parcel.reason === 'unsupported'
        ? t('send.unsupported')
        : parcel.reason === 'bytes'
          ? t('send.noBytes', { status: parcel.status })
          : t('send.failed', { code: parcel.code });
    return;
  }
  const outcome = await send(parcel.data);
  // `expired` garde son colis : le prochain clic partira sans attente, donc il
  // aboutira. C'est la seule erreur de cette famille qui se répare toute seule.
  if (outcome.kind === 'expired') error.value = t('send.expired');
  else if (outcome.kind === 'failed') error.value = t('send.failed', { code: outcome.code });
}

/**
 * **Envoyer un lien** — la miniature, et l'adresse de la photo dans un partage
 * *(L1, 2026-09-14)*.
 *
 * Même règle que `onSend` : quand la miniature est déjà prête — le menu l'a
 * cherchée à son ouverture —, le colis se compose et part **sans un `await`**.
 */
export async function onSendLink(entity: Entity, aid: string): Promise<void> {
  const what = linkableOf(entity);
  const fid = entity.Fid;
  if (!what || !fid) return;
  const url = linkUrl(location.origin, { aid, fid });

  const ready = thumbFor(fid);
  // **Aucun `await` sur ce chemin** — même raison que dans `onSend`.
  if (ready) return handOver(linkParcel(ready, url));

  busy.value = t('send.preparing');
  error.value = undefined;
  try {
    const thumb = await prepareThumb(entity, what);
    return await handOver(thumb.ok ? linkParcel(thumb.file, url) : thumb);
  } finally {
    busy.value = undefined;
  }
}

/**
 * **Ouvrir un lien reçu** : le partage, puis la photo par-dessus.
 *
 * La garde vient d'abord, et c'est `GET /albums/{aid}/items` qui la tient : si le
 * partage ne se lit pas, **on ne navigue pas**. Tout refus — partage inconnu,
 * supprimé, ou exposé à un cercle dont on n'est pas — rend **la même phrase** :
 * les distinguer renseignerait le destinataire sur ce qu'il ne peut pas voir.
 *
 * La page est relue une seconde fois par `applyView`, qui recharge toute
 * collection à l'arrivée. Une lecture de plus, une fois par lien ouvert, contre
 * un chemin de chargement parallèle à celui de tous les autres écrans.
 */
export async function onOpenLink(target: LinkTarget): Promise<void> {
  busy.value = t('link.opening');
  error.value = undefined;
  try {
    let page;
    try {
      page = await listAlbumItems(target.aid);
    } catch (err) {
      if (err instanceof ApiError) {
        error.value = t('link.unavailable');
        return;
      }
      throw err;
    }
    albumItems.value = page.items;
    openView({ kind: 'collection', aid: target.aid });

    // Dans la page, la visionneuse parcourt le partage. Au-delà des 200 premiers
    // éléments — ou filtrée —, la photo n'y est pas : on la relit seule, et
    // c'est encore le serveur qui dit si on la voit.
    const listed = items.value.find((e) => e.Fid === target.fid);
    if (listed) return onOpen(listed, items.value);
    try {
      const entity = await getFile(target.fid);
      onOpen(entity, [entity]);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      error.value = t('link.photoGone');
    }
  } catch (err) {
    error.value = messageOf(err);
  } finally {
    busy.value = undefined;
  }
}

/**
 * Télécharger.
 *
 * Une ancre `download` plutôt qu'une navigation : l'URL présignée porte un
 * `content-disposition` signé côté serveur, et le navigateur doit l'honorer
 * sans quitter la page — sortir de l'application pour récupérer un fichier
 * perdrait l'état de l'écran.
 */
export function onDownload(entity: Entity, role: string): Promise<void> {
  const fid = entity.Fid;
  if (!fid) return Promise.resolve();
  return act(t('inspector.preparing'), async () => {
    const { url } = await getFileUrl(fid, role);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.rel = 'noopener';
    anchor.download = entity.Name;
    anchor.click();
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// L'éditeur de notes (4.3)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ouvrir une note en édition.
 *
 * La fiche complète est relue avant d'ouvrir, et pas par prudence : le miroir ne
 * porte que ce que `/changes` transporte, et **`ContentRev` y est indispensable**
 * — c'est le `baseRev` du verrou. Ouvrir sur une révision périmée ferait échouer
 * le premier enregistrement en `stale_rev` alors que personne n'a rien touché.
 */
export function onEdit(entity: Entity): Promise<void> {
  const fid = entity.Fid;
  if (!fid || entity.Type !== 'NOTE') return Promise.resolve();

  return act(t('editor.opening'), async () => {
    const full = await getFile(fid);
    // **Le serveur décide qui écrit, et il le dit ici** *(2026-08-16)*. La relecture
    // avait déjà lieu ; elle porte désormais aussi le droit. C'est la dernière
    // barrière avant l'éditeur, et la seule qui soit exacte : un client peut avoir
    // en main une entité lue avant que le partage ne se referme.
    if (full.canWriteContent === false) {
      error.value = t('collection.readOnlyNote');
      return;
    }
    const text = await readNote(full);
    reader.value = undefined;
    pushOverlay('editor');
    editor.value = {
      entity: full,
      text,
      saved: text,
      baseRev: revOf(full),
      mode: 'rich',
      saving: false,
      loaded: 0,
    };
  });
}

/**
 * Enregistrer. **Sur événement, jamais à la frappe** (D20) : chaque
 * enregistrement écrit un item `REV#`, et une sauvegarde continue en produirait
 * un par caractère — un historique illisible et une facture de stockage pour
 * rien.
 *
 * Ne rend rien : le résultat vit dans l'état de l'éditeur, parce que trois des
 * quatre issues ne sont pas des erreurs mais des **situations à trancher par
 * l'utilisateur**, et qu'un bandeau rouge générique les confondrait.
 */
export async function onSaveNote(): Promise<boolean> {
  const state = editor.value;
  if (!state || state.saving) return false;
  // Rien à enregistrer n'est pas un échec : c'est le cas le plus fréquent à la
  // fermeture, et écrire quand même coûterait une révision par consultation.
  if (state.text === state.saved) return true;

  patchEditor({ saving: true, conflict: undefined });
  error.value = undefined;
  try {
    const outcome = await saveNote(state.entity, state.text, state.baseRev);
    if (!outcome.ok) {
      patchEditor({ saving: false, conflict: conflictOf(outcome) });
      return false;
    }

    // La base avance **avec** le texte enregistré : c'est ce couple qui permet
    // un second enregistrement dans la même session sans relire l'entité. Ce
    // qu'elle devient est écrit dans `notes.ts` — c'est le seul endroit d'où une
    // mesure peut l'atteindre sans compte *(N5)*.
    const updated = afterSave(state.entity, outcome);
    patchEditor({
      saving: false,
      saved: state.text,
      baseRev: outcome.rev,
      entity: updated,
      // L'historique affiché est périmé dès qu'une révision est écrite ; le
      // garder montrerait une liste à laquelle il manque la dernière ligne.
      history: undefined,
    });
    mergeEntities([updated]);
    // Le colis d'un envoi porte le **texte d'avant** : le garder enverrait la
    // note telle qu'elle était au clic précédent.
    forgetParcel();
    await session.cache?.put([{ key: state.entity.Fid ?? '', value: updated }]);
    if (detail.value && idOf(detail.value) === state.entity.Fid) detail.value = updated;
    return true;
  } catch (err) {
    patchEditor({ saving: false });
    error.value = messageOf(err);
    return false;
  }
}

const conflictOf = (outcome: Exclude<SaveOutcome, { ok: true }>): ConflictState => {
  switch (outcome.reason) {
    case 'stale':
      return { kind: 'stale', currentRev: outcome.currentRev, remote: outcome.remote };
    case 'locked':
      return { kind: 'locked', expiresAt: outcome.expiresAt };
    case 'lockLost':
      return { kind: 'lockLost', currentRev: outcome.currentRev };
    case 'missingBytes':
      return { kind: 'missingBytes' };
  }
};

/**
 * Reprendre après un conflit : **relire, et repartir de la version distante**.
 *
 * Aucune fusion automatique n'est tentée. Un merge 3-voies sur du Markdown
 * produit des blocs dupliqués et des suppressions annulées, et le faire en
 * silence est exactement la perte de données que le verrou existe pour rendre
 * visible. Le texte local n'est pas jeté : l'écran l'a toujours sous la main.
 */
export function onReloadNote(): Promise<void> {
  const state = editor.value;
  if (!state) return Promise.resolve();
  const fid = state.entity.Fid ?? '';

  return act(t('editor.reloading'), async () => {
    const full = await getFile(fid);
    const text = await readNote(full);
    editor.value = {
      ...state,
      entity: full,
      text,
      saved: text,
      baseRev: revOf(full),
      conflict: undefined,
      history: undefined,
      // Le texte vient d'ailleurs que du clavier : l'éditeur en clair doit
      // repeindre son document, et c'est le seul signal qui l'y autorise.
      loaded: state.loaded + 1,
    };
    mergeEntities([full]);
  });
}

/**
 * L'historique, **au propriétaire seul** (D22). L'appel rendrait `403` sur un
 * contenu reçu ; l'interface n'affiche donc pas un bouton grisé mais aucun.
 */
export function onLoadHistory(): Promise<void> {
  const state = editor.value;
  if (!state) return Promise.resolve();
  return act(t('editor.loadingHistory'), async () => {
    const page = await listVersions(state.entity.Fid ?? '');
    patchEditor({ history: page.items });
  });
}

/**
 * Restaurer une révision. Elle est **réenregistrée**, donc `ContentRev` avance :
 * un retour en arrière est lui-même une révision, et donc lui-même annulable.
 * Faire reculer le compteur casserait le verrou de tout appareil déjà en avance.
 */
export function onRestore(rev: number): Promise<void> {
  const state = editor.value;
  if (!state) return Promise.resolve();
  return act(t('editor.restoring'), async () => {
    const text = await readRevision(state.entity.Fid ?? '', rev);
    // On passe par l'état plutôt que par `restoreRevision` : l'utilisateur doit
    // voir ce qu'il restaure **avant** que ce soit écrit, et garder la main.
    patchEditor({ text, history: undefined, loaded: state.loaded + 1 });
  });
}

/**
 * Fermer. **Enregistre d'abord**, et ne ferme que si c'est passé : fermer sur un
 * conflit jetterait le texte en cours sans que personne l'ait décidé.
 *
 * **Mais une sortie qui échoue toujours est une porte murée** *(2026-08-16)*. La
 * règle ci-dessus supposait que l'enregistrement finit par passer ; sur une note
 * qu'on n'a pas le droit d'écrire il rend `403` à chaque fois, et la croix, `Échap`
 * et le geste système repartaient tous par ce chemin — on ne sortait plus de
 * l'écran, sauf à recharger le site. Le geste de retour aggravait même la
 * situation : `dismissOverlay` dépile *avant* d'appeler, donc chaque tentative
 * consommait une entrée d'historique sans rien refermer.
 *
 * La deuxième demande sort donc **sans enregistrer**, et l'écran l'annonce entre
 * les deux plutôt que de le décider tout seul : jeter du texte en silence serait
 * le défaut symétrique.
 */
export async function onCloseEditor(): Promise<void> {
  const state = editor.value;
  if (!state) return;
  if (state.leaving) {
    editor.value = undefined;
    return;
  }

  const saved = await onSaveNote();
  if (saved) {
    editor.value = undefined;
    return;
  }
  patchEditor({ leaving: true });
}

/** Rester : on retire la porte de sortie, on ne touche pas au texte. */
export function onStayInEditor(): void {
  patchEditor({ leaving: false });
}

// ─────────────────────────────────────────────────────────────────────────────
// Photothèque, albums et réactions (4.4)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Les albums, **à la demande**. Comme les groupes, ils ne sont pas dans le miroir :
 * `GET /albums` interroge deux familles de partitions — les siennes et celles de
 * ses groupes — que `/changes` ne draine pas de la même façon.
 */
export async function loadAlbums(force = false): Promise<void> {
  if (albums.value !== undefined && !force) return;
  const page = await listAlbums();
  albums.value = page.items;
}

/**
 * Ouvrir un album. Son contenu vient de `GET /albums/{aid}/items`, **jamais du
 * miroir** : l'appartenance à un album est portée par des arêtes, et le miroir ne
 * porte que des entités. Filtrer le miroir donnerait un album toujours vide.
 */
/**
 * **Cocher « Doublons » dans un partage y ramène les empreintes** *(DB12)*.
 *
 * La page a été chargée sans elles — c'est le cas courant —, donc il faut la
 * relire pour que la comparaison ait de quoi travailler. Décocher ne relit rien :
 * ce qu'on a déjà ne gêne personne.
 */
export function reloadAlbumItems(): void {
  const v = view.value;
  if (v.kind === 'collection') void loadAlbumItems(v.aid);
}

export function onOpenAlbum(aid: string): Promise<void> {
  // Une collection **est un endroit** : elle passe par `openView`, donc elle
  // s'empile comme un dossier ou un groupe — sans quoi elle serait le seul écran
  // dont on ne peut pas revenir. Le chargement de son contenu vit dans
  // `applyView`, et c'est ce qui compte : un **retour** vers une collection doit
  // recharger *ses* éléments. Sans cela, revenir d'une collection à une autre
  // laissait à l'écran le contenu de celle qu'on venait de quitter, sous le titre
  // de celle qu'on regardait.
  openView({ kind: 'collection', aid });
  return Promise.resolve();
}

/**
 * Le contenu d'une collection, appelé par `applyView` — aller comme retour.
 *
 * **Les empreintes ne sont demandées qu'en mode doublons** *(DB12)* : elles
 * coûtent une lecture groupée de la page côté serveur, et le mode est un
 * interrupteur qu'on n'active presque jamais.
 */
function loadAlbumItems(aid: string): Promise<void> {
  clearPicked();
  return act(t('collection.loading'), async () => {
    const page = await listAlbumItems(aid, 200, duplicatesMode.value);
    // La vue a pu changer pendant l'aller-retour — un retour rapide, un clic sur
    // une autre collection : poser un contenu périmé sous un autre titre est
    // exactement le défaut qu'on corrige.
    const v = view.value;
    if (v.kind === 'collection' && v.aid === aid) albumItems.value = page.items;
  });
}

/**
 * Créer une collection, **déjà attachée à son groupe**.
 *
 * Le `gid` n'est pas optionnel, et c'est la décision produit de 4.5 : une
 * collection existe pour être partagée. Sans groupe, elle serait un troisième
 * mode de regroupement — après le dossier et le groupe — qu'il faudrait ensuite
 * expliquer, et un ensemble privé se fait très bien avec un dossier.
 *
 * Deux appels, et l'ordre compte : `POST /albums` crée, `POST …/groups` attache.
 * Le second rend `202`, mais l'arête d'accès et `AuthGroups` sont écrits
 * **synchroniquement** — seule la propagation aux éléments est différée. La
 * collection est donc visible dans son groupe dès le retour, ce dont dépend le
 * fait qu'elle apparaisse sans attendre un drainage de `/changes`.
 */
export function onCreateAlbum(name: string, gid: string): Promise<void> {
  return act(t('collection.creating'), async () => {
    const aid = ulid();
    // **Pas de dossier** : une collection n'est pas rangée. Le serveur rendrait
    // `400 folder_unsupported`, et c'est la bonne réponse — l'emplacement et
    // l'ensemble ne sont pas deux façons de faire la même chose.
    await createAlbum({ aid, name });
    await shareAlbum(aid, [gid]);
    // **La création rend `{ aid }` et rien d'autre**, et la liste se peint depuis
    // le miroir : sans relecture, la collection n'apparaissait qu'au drainage
    // suivant de `/changes`, dont la propagation n'a aucune borne documentée. On
    // la crée, on ne la voit pas — et rien ne dit pourquoi. La relecture est
    // **après** l'attachement : elle doit ramener `authGroups`, sans quoi la
    // collection naîtrait dans aucun groupe à l'écran.
    await adopt([await getAlbum(aid)]);
    await loadAlbums(true);
  });
}

/**
 * Ajouter la sélection à un album — **un rapport par fid**, jamais un verdict
 * global. Un `already` est un succès : l'état voulu est atteint. Un `denied` ne
 * doit surtout pas être rejoué automatiquement (`edge_removed` fermerait sinon la
 * résurrection par rejeu qu'il vient d'empêcher) : il se dit, et c'est tout.
 */
export function onAddToAlbum(aid: string, fids: readonly string[]): Promise<void> {
  return act(t('collection.adding'), async () => {
    const res = await addAlbumItems(aid, fids);
    const refused = res.results.filter((r) => r.status === 'denied');
    // **Après l'appel, jamais avant** *(2026-08-16)* : le sélecteur rouvrira sur
    // ce partage, et proposer d'emblée celui où le dernier versement a été refusé
    // ferait répéter l'échec. Un refus partiel (`denied` sur quelques `fid`) reste
    // un versement réussi — c'est bien le partage qu'on visait.
    rememberLastAlbum(me.value?.uid, aid);
    clearPicked();
    if (view.value.kind === 'collection' && view.value.aid === aid) {
      const page = await listAlbumItems(aid);
      albumItems.value = page.items;
    }
    await loadAlbums(true);
    if (refused.length > 0) {
      error.value = t('collection.someRefused', {
        count: refused.length,
        code: refused[0]?.code ?? 'denied',
      });
    }
  });
}

/**
 * **Supprimer une collection** — le geste que la route servait sans qu'aucun
 * écran ne l'atteigne.
 *
 * Ce que la suppression coupe est plus large que ce que le mot laisse croire, et
 * c'est ce que l'écran doit dire avant : le serveur tombstone l'album **et
 * révoque toutes les arêtes d'accès qu'il avait propagées**, par cercle. Les
 * membres perdent donc la lecture de chaque fichier qui n'était exposé que par
 * elle — y compris ses octets. Ce qu'elle contenait, en revanche, n'est pas
 * supprimé : c'est « retirer n'est pas supprimer », appliqué à l'ensemble entier.
 *
 * On **remonte d'un cran avant** de recharger : rester sur l'écran d'une
 * collection qui n'existe plus laisserait une grille vide sous son ancien nom.
 */
export function onDeleteAlbum(aid: string): Promise<void> {
  return act(t('collection.deleting'), async () => {
    await deleteAlbum(aid);
    const up = parentOf(view.value);
    albums.value = (albums.value ?? []).filter((a) => a.Aid !== aid);
    albumItems.value = [];
    openView(up ?? { kind: 'shared' });
    // La collection vivait aussi dans le miroir — elle y est arrivée par
    // `/changes` ou par la relecture de sa création. L'y laisser la ferait
    // réapparaître au prochain rendu de la liste des groupes.
    forgetEntity(aid);
    await session.cache?.put([{ key: aid, value: null }]);
    await loadAlbums(true);
  });
}

/**
 * **Qui peut verser dans une collection** (`ContribPolicy`) — le troisième réglage
 * que le socle servait et qu'aucun client n'atteignait.
 *
 * `members` est ce qui fait exister la collection **collective** : chaque membre
 * des cercles destinataires y ajoute ses propres objets. Le serveur garde la
 * dissymétrie qui compte — un contributeur n'y verse que ce qui est à lui, et
 * n'en retire que ce qu'il a versé.
 */
export function onSetContribPolicy(aid: string, policy: 'owner' | 'members'): Promise<void> {
  return act(t('collection.savingPolicy'), async () => {
    const updated = await patchAlbum(aid, { contribPolicy: policy });
    await adopt([updated]);
    albums.value = (albums.value ?? []).map((a) => (a.Aid === aid ? { ...a, ...updated } : a));
  });
}

/**
 * Retirer d'une collection. **Ce n'est pas une suppression** — la phrase est dans
 * l'écran, pas seulement dans la documentation : l'arête est tombstonée, et
 * l'élément reste rangé dans son dossier et dans ses autres collections.
 */
export function onRemoveFromAlbum(aid: string, fid: string): Promise<void> {
  return act(t('collection.removing'), async () => {
    await removeAlbumItem(aid, fid);
    albumItems.value = albumItems.value.filter((e) => e.Fid !== fid);
  });
}

/** Supprimer la sélection, une par une : il n'existe pas de route de lot. */
export function onDeleteSelection(fids: readonly string[]): Promise<void> {
  return act(t('gallery.deletingSelection', { count: fids.length }), async () => {
    for (const fid of fids) {
      await deleteFile(fid);
      forgetEntity(fid);
      await session.cache?.put([{ key: fid, value: null }]);
    }
    clearPicked();
    await refreshFolders();
  });
}

/**
 * **Se nommer** — `PUT /profile { alias }`.
 *
 * C'est l'incrément 4.8, et son enjeu dépasse le confort : jusqu'ici l'alias
 * n'était écrit que par la route d'administration **à la création du compte**, si
 * bien qu'un nom faux y était définitif — et pas seulement pour soi. Le nom est la
 * **seule chose qui désigne une personne** dans un groupe.
 *
 * **Aucune propagation.** L'alias n'est pas projeté sur les arêtes : il est relu à
 * la réponse par le serveur, à chaque lecture qui nomme quelqu'un. Le changer ne
 * demande donc rien d'autre que de le réécrire, là où renommer un objet
 * déclencherait un fan-out.
 *
 * Vider le champ **retire** l'alias plutôt que d'écrire une chaîne vide : c'est ce
 * que le serveur attend, et une chaîne vide passerait pour un nom — un nom
 * invisible, qu'aucun écran ne saurait remplacer par « Sans nom ».
 */
export function onSaveAlias(alias: string): Promise<void> {
  return act(t('me.savingAlias'), async () => {
    const trimmed = alias.trim();
    const profile = await putProfile(trimmed === '' ? null : trimmed);
    const current = me.value;
    if (current) {
      me.value = {
        ...current,
        ...(profile.alias === null ? {} : { alias: profile.alias }),
        avatarRev: profile.avatarRev,
      };
      if (profile.alias === null) {
        const { alias: _retiré, ...sansAlias } = me.value;
        me.value = sansAlias;
      }
    }
  });
}

/**
 * **Se donner un visage** — le protocole d'octets, en deux temps.
 *
 * Il n'y en a que deux là où un fichier en compte trois : l'avatar n'a pas
 * d'entité à créer, il a une **clé unique par personne**. Reste ce qui ne change
 * jamais — le `PUT` va directement à S3, et c'est le serveur qui **constate** les
 * octets (I-16), lui seul.
 *
 * Le constat est ici plus qu'une formalité comptable. L'avatar est le vecteur le
 * plus large du système — une image qui s'affiche chez tous les membres de tous
 * les groupes — et **personne ne la décode côté serveur** : le `complete` en relit
 * donc le format dans les octets (W2) et les dimensions dans l'en-tête (W3). Ses
 * refus sont des `409` porteurs de sens, pas des pannes : `format_rejected`,
 * `pixels_exceeded`, `size_exceeded`.
 *
 * Les en-têtes signés se renvoient **tels quels** (I-21), comme partout ailleurs.
 */
export function onPickAvatar(file: File): Promise<void> {
  return act(t('me.savingAvatar'), async () => {
    const mime = file.type || 'application/octet-stream';
    const { url, headers, rev } = await startAvatarUpload(mime);

    const res = await fetch(url, { method: 'PUT', headers, body: file });
    if (!res.ok) throw new Error(`envoi refusé par S3 (${res.status}) : ${await res.text()}`);

    const profile = await completeAvatarUpload(rev);
    // La révision change, donc les clés de cache changent ; on oublie quand même
    // les anciennes, sans quoi elles vivraient jusqu'au rechargement de la page.
    forgetAvatar(profile.uid);
    const current = me.value;
    if (current) me.value = { ...current, avatarRev: profile.avatarRev };
  });
}

/**
 * **Réagir** (D24) — poser ou changer *sa* entrée.
 *
 * `patch` est une **modification de son entrée**, pas le corps envoyé : le serveur
 * fait `SET Reactions.<uid> = <valeur>`, donc envoyer `{ score }` seul **efface
 * l'emoji** posé auparavant par la même personne. C'est un piège réel — mettre 4
 * étoiles sur une photo qu'on avait aimée retirait le cœur, sans rien afficher. La
 * fusion se fait donc ici, une fois, plutôt que dans chaque bouton.
 *
 * Une entrée qui deviendrait vide n'est pas envoyable : `parseReaction` exige au
 * moins un des deux champs. C'est un **retrait**, et c'est la route de retrait qui
 * l'exprime.
 */
export function onReact(
  fid: string,
  patch: { emoji?: string | undefined; score?: number | undefined },
): Promise<void> {
  return act(t('reaction.saving'), async () => {
    // `reactions.mine` **est** son entrée, servie par le serveur : pas besoin de
    // la retrouver par `uid` dans une map nominative qui, elle, ne sort jamais.
    const mine = detail.value?.reactions?.mine ?? {};
    const next: { emoji?: string; score?: number } = { ...mine, ...patch };
    if (next.emoji === undefined && next.score === undefined) {
      const cleared = await deleteReaction(fid);
      adoptReaction(fid, cleared);
      return;
    }
    const updated = await putReaction(fid, {
      ...(next.emoji !== undefined ? { emoji: next.emoji } : {}),
      ...(next.score !== undefined ? { score: next.score } : {}),
    });
    adoptReaction(fid, updated);
  });
}

export function onUnreact(fid: string): Promise<void> {
  return act(t('reaction.removing'), async () => {
    adoptReaction(fid, await deleteReaction(fid));
  });
}

/**
 * Recopie l'entité **rendue par l'écriture** dans la fiche, le miroir et l'album.
 * Les trois, parce que les filtres de la photothèque portent sur `Reactions` : ne
 * toucher que la fiche ferait disparaître une photo de son propre filtre sans
 * qu'elle bouge à l'écran.
 *
 * La réponse fait autorité — la reconstruire localement était le chemin le plus
 * court vers deux vérités qui divergent.
 */
function adoptReaction(fid: string, fresh: Entity): void {
  const current = detail.value;
  if (current && idOf(current) === fid) detail.value = fresh;

  // Le miroir garde son entité et n'emprunte que `reactions` : la réponse est
  // filtrée par le sérialiseur, et écraser l'entité entière retirerait `Folder`
  // sur un objet reçu — donc le sortirait de son dossier à l'écran.
  const known = entities.value.find((e) => e.Fid === fid);
  if (known) {
    const next: Entity = { ...known, reactions: fresh.reactions };
    mergeEntities([next]);
    void session.cache?.put([{ key: fid, value: next }]);
  }
  albumItems.value = albumItems.value.map((e) =>
    e.Fid === fid ? { ...e, reactions: fresh.reactions } : e,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Groupes, membres et souveraineté (4.6)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Créer un groupe. Le `gid` vient du client (D14) : le cercle a une identité avant
 * d'atteindre le serveur, et un rejeu ne crée pas de doublon.
 *
 * Créer un groupe, c'est y entrer — la transaction écrit l'appartenance du
 * créateur avec le méta, sans quoi le groupe serait un objet que plus personne ne
 * pourrait ni lire ni supprimer.
 */
export function onCreateGroup(name: string): Promise<void> {
  return act(t('group.creating'), async () => {
    const gid = ulid();
    await createGroup(gid, name);
    await refreshGroups();
    view.value = { kind: 'group', gid, tab: 'members' };
    pushView(view.value);
    // On atterrit sur **les membres** : un cercle sans personne n'a rien à
    // partager, et la première chose à faire est donc d'y inviter quelqu'un.
    await loadMembers(gid, true);
  });
}

/** Relit la face autoritative : effectif et date viennent du méta, pas du cache. */
export async function refreshGroups(): Promise<void> {
  groups.value = (await getGroups()).items;
}

export function onRenameGroup(gid: string, name: string): Promise<void> {
  return act(t('group.renaming'), async () => {
    await patchGroup(gid, name);
    await refreshGroups();
  });
}

/**
 * Supprimer un groupe — **définitif, et il n'y a pas de corbeille pour un cercle**.
 *
 * Le groupe et **toutes** ses appartenances sont tombstonés dans une seule
 * transaction : chaque membre en sort à l'instant même et le perd de ses propres
 * appareils. Rien ne le restaure, et c'est pourquoi l'écran demande confirmation
 * plutôt que de faire confiance au clic.
 */
export function onDeleteGroup(gid: string): Promise<void> {
  return act(t('group.deleting'), async () => {
    await deleteGroup(gid);
    await refreshGroups();
    view.value = { kind: 'shared' };
    // Le cercle n'existe plus : l'entrée qui le décrivait est **remplacée**, sans
    // quoi un retour rouvrirait un groupe supprimé.
    replaceView(view.value);
  });
}

/**
 * Inviter, **par adresse** : c'est le seul identifiant qu'un humain connaisse de
 * quelqu'un d'autre, le `uid` étant opaque par construction (D13).
 *
 * `404 user_unknown` n'est pas une panne mais une réponse : le compte n'existe
 * pas, et l'identité ne naît **que** d'un acte d'admin (D34). L'écran le dit dans
 * ces termes plutôt qu'en code, faute de quoi on cherche l'erreur de frappe.
 */
export function onAddMember(gid: string, email: string): Promise<void> {
  return act(t('group.inviting'), async () => {
    await addMember(gid, email);
    await Promise.all([loadMembers(gid, true), refreshGroups()]);
  });
}

export function onRemoveMember(gid: string, uid: string): Promise<void> {
  return act(t('group.removing'), async () => {
    await removeMember(gid, uid);
    await Promise.all([loadMembers(gid, true), refreshGroups()]);
  });
}

/**
 * **Quitter** (D16) — sans dépendre de la bonne volonté du propriétaire.
 *
 * Le serveur refuse en `409 last_owner` de laisser partir le dernier propriétaire :
 * un groupe sans propriétaire serait un objet que plus personne ne peut administrer.
 */
export function onLeaveGroup(gid: string): Promise<void> {
  return act(t('group.leaving'), async () => {
    await leaveGroup(gid);
    await refreshGroups();
    view.value = { kind: 'shared' };
    replaceView(view.value);
    // Ce que le groupe portait cesse d'être à nous : le drainage suivant en fera
    // la purge locale, mais l'écran ne doit pas montrer un cercle qu'on a quitté.
    await pullChanges();
  });
}

/**
 * **« Où va ma donnée »** (D15) — chargée à la demande, jamais avec la fiche.
 *
 * Deux lectures serveur (les arêtes, puis le journal) pour une question qu'on ne
 * pose pas à chaque sélection : les attacher à `select()` les aurait payées sur
 * chaque clic d'une grille.
 */
export function onLoadShares(fid: string): Promise<void> {
  return act(t('share.loading'), async () => {
    const res = await getShares(fid);
    shares.value = {
      fid,
      shares: res.shares,
      excluded: res.excludedGroups,
      history: res.history,
    };
  });
}

/**
 * **Le retrait souverain** (D15) — couper l'accès d'un groupe à un objet.
 *
 * Il ne retire pas *une* raison mais **toutes** : ni le partage direct ni aucune
 * collection ne le rétablit, parce que le groupe entre en exclusion. C'est ce qui
 * le distingue d'un simple retrait de collection — lequel laisserait la propagation
 * suivante rouvrir l'accès, et le réconciliateur avec elle.
 */
export function onRevokeGroup(fid: string, gid: string): Promise<void> {
  return act(t('share.revoking'), async () => {
    await removeFromGroup(fid, gid);
    await onLoadShares(fid);
  });
}

/** Lever un blocage **sans repartager** : rendre le droit demande un acte neuf. */
export function onUnblockGroup(fid: string, gid: string): Promise<void> {
  return act(t('share.unblocking'), async () => {
    await removeFromGroup(fid, gid, true);
    await onLoadShares(fid);
  });
}

/**
 * **Qui a réagi**, au propriétaire seul. La liste est chargée à l'ouverture du
 * panneau : elle ne sert qu'à modérer, et l'agrégat suffit à tout le reste.
 */
export function onLoadReactors(fid: string): Promise<void> {
  return act(t('reaction.loadingPeople'), async () => {
    reactors.value = { fid, items: (await listReactions(fid)).items };
  });
}

/**
 * **Modérer** — retirer de son propre objet la réaction d'un autre.
 *
 * La route existait depuis 4.4 et n'avait aucun appelant possible : le `uid` visé
 * ne sortait d'aucune réponse. C'est `GET /files/{fid}/reactions` qui la rend
 * formable, et c'est pourquoi les deux arrivent ensemble.
 */
export function onModerateReaction(fid: string, uid: string): Promise<void> {
  return act(t('reaction.moderating'), async () => {
    const fresh = await moderateReaction(fid, uid);
    adoptReaction(fid, fresh);
    reactors.value = {
      fid,
      items: (reactors.value?.items ?? []).filter((r) => r.uid !== uid),
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Administration
// ─────────────────────────────────────────────────────────────────────────────

export const adminUsers = signal<readonly AdminUser[]>([]);
export const adminStats = signal<AdminStats | undefined>(undefined);
export const adminBusy = signal<boolean>(false);
/**
 * **Une écriture d'admin a été refusée faute d'authentification récente** (D21).
 * Ce n'est pas une erreur : c'est une étape, et l'écran doit proposer la sortie
 * plutôt qu'un message.
 */
export const sudoNeeded = signal<boolean>(false);

/** Le tableau de bord et l'annuaire, en une fois : la console s'ouvre sur les deux. */
export async function loadAdminConsole(): Promise<void> {
  adminBusy.value = true;
  try {
    const [stats, page] = await Promise.all([getAdminStats(), listAdminUsers()]);
    adminStats.value = stats;
    adminUsers.value = page.items;
  } catch (err) {
    error.value = messageOf(err);
  } finally {
    adminBusy.value = false;
  }
}

/**
 * **Repartir vers le managed login en exigeant une saisie**, puis revenir ici.
 *
 * `prompt=login` est ce qui distingue « vous avez une session » de « vous êtes
 * là » : sans lui, Cognito rendrait immédiatement un code sur la session en
 * cours, et le `auth_time` du jeton neuf serait celui de la connexion d'origine
 * — la ré-authentification n'aurait rien authentifié.
 */
export function reauthenticate(): Promise<never> {
  return beginLogin({ prompt: 'login', returnTo: 'admin' });
}

/**
 * Le sudo, du point de vue de l'appelant : on **évite l'aller-retour** quand on
 * sait déjà que l'authentification est trop vieille, et on rattrape le
 * `sudo_required` du serveur dans le cas contraire.
 *
 * Les deux moitiés sont nécessaires : la vérification locale est une commodité
 * — elle lit un jeton que personne n'a vérifié —, et l'autorité reste le refus
 * du serveur. Sans la première, chaque première action d'une session échouerait
 * une fois ; sans la seconde, un client bricolé passerait.
 */
async function withSudo(fn: () => Promise<void>): Promise<boolean> {
  const session = await currentSession();
  const age = session ? authAgeSeconds(session) : null;
  if (age === null || age > SUDO_MAX_AGE) {
    sudoNeeded.value = true;
    return false;
  }

  try {
    await fn();
    sudoNeeded.value = false;
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.code === 'sudo_required') {
      sudoNeeded.value = true;
      return false;
    }
    error.value = messageOf(err);
    return false;
  }
}

/** Cinq minutes — la même borne que le serveur, qui reste seul juge. */
const SUDO_MAX_AGE = 300;

export async function onCreateAccount(
  email: string,
  alias: string | undefined,
  role: 'user' | 'admin',
): Promise<boolean> {
  let cree = false;
  await act(t('admin.creating'), async () => {
    cree = await withSudo(async () => {
      await createAdminUser(email, alias, role);
      await loadAdminConsole();
    });
  });
  return cree;
}

/**
 * **Renvoyer quelqu'un vers son mot de passe**, et dire lequel des deux courriers
 * est parti.
 *
 * Rend le `mode` du serveur, ou `undefined` si rien n'est parti — refus de sudo
 * compris. Le retour n'est pas un luxe : « c'est fait » sans dire *quoi* laisserait
 * chercher un code dans un courrier qui porte un mot de passe provisoire.
 *
 * Aucune relecture de la console : ni les compteurs ni la ligne ne bougent, un
 * courrier n'est pas un changement d'état.
 */
export async function onResetAccountPassword(
  uid: string,
): Promise<'invitation' | 'reset' | undefined> {
  let mode: 'invitation' | 'reset' | undefined;
  await act(t('admin.resetting'), async () => {
    await withSudo(async () => {
      const res = await resetAdminUserPassword(uid);
      mode = res.mode;
    });
  });
  return mode;
}

/**
 * **Le rattrapage des descriptions** *(R5)* — et son **dry run** d'abord : combien de
 * photos, avant de dépenser quoi que ce soit.
 *
 * La route rend la main par tranches ; l'écran les enchaîne et **garde le curseur**
 * entre deux appuis. Une session d'admin ne vaut que cinq minutes (D21) : si elle
 * expire au milieu d'un long parcours, la ré-authentification ne le fait pas
 * recommencer — c'est ce que le curseur gardé achète. Rejouer serait sans danger
 * (la Lambda relit chaque entité), mais ce serait payer deux fois le parcours.
 */
export const backlog = signal<
  | { readonly dryRun: boolean; readonly upgrade: boolean; readonly selected: number; readonly enqueued: number; readonly scanned: number; readonly cursor: string | null; readonly done: boolean }
  | undefined
>(undefined);

export async function onDescribeBacklog(opts: { dryRun: boolean; upgrade: boolean }): Promise<void> {
  const current = backlog.value;
  const resume =
    current && !current.done && current.dryRun === opts.dryRun && current.upgrade === opts.upgrade
      ? current
      : undefined;
  await act(t(opts.dryRun ? 'admin.describeCounting' : 'admin.describeQueuing'), async () => {
    await withSudo(async () => {
      let state = resume ?? { ...opts, selected: 0, enqueued: 0, scanned: 0, cursor: null, done: false };
      do {
        const page = await describeBacklogRoute({
          ...opts,
          ...(state.cursor ? { cursor: state.cursor } : {}),
        });
        state = {
          ...state,
          selected: state.selected + page.selected,
          enqueued: state.enqueued + page.enqueued,
          scanned: state.scanned + page.scanned,
          cursor: page.next,
          done: page.next === null,
        };
        backlog.value = state;
      } while (state.cursor);
    });
  });
}

/**
 * **Le rattrapage des empreintes visuelles** *(K3)* — même forme que celui des
 * descriptions : compter, puis enfiler, par tranches, en gardant le curseur entre
 * deux appuis. Rien de payant ici — une lecture de vignette par photo —, mais la
 * route reste un acte d'administration (D21).
 */
export const visualHashProgress = signal<
  | { readonly dryRun: boolean; readonly selected: number; readonly enqueued: number; readonly scanned: number; readonly cursor: string | null; readonly done: boolean }
  | undefined
>(undefined);

export async function onVisualHashBacklog(dryRun: boolean): Promise<void> {
  const current = visualHashProgress.value;
  const resume = current && !current.done && current.dryRun === dryRun ? current : undefined;
  await act(t(dryRun ? 'admin.visualHashCounting' : 'admin.visualHashQueuing'), async () => {
    await withSudo(async () => {
      let state = resume ?? { dryRun, selected: 0, enqueued: 0, scanned: 0, cursor: null, done: false };
      do {
        const page = await visualHashBacklogRoute({ dryRun, ...(state.cursor ? { cursor: state.cursor } : {}) });
        state = {
          ...state,
          selected: state.selected + page.selected,
          enqueued: state.enqueued + page.enqueued,
          scanned: state.scanned + page.scanned,
          cursor: page.next,
          done: page.next === null,
        };
        visualHashProgress.value = state;
      } while (state.cursor);
    });
  });
}

export async function onSetAccountStatus(
  uid: string,
  status: 'active' | 'disabled',
): Promise<void> {
  await act(t(status === 'disabled' ? 'admin.disabling' : 'admin.enabling'), async () => {
    await withSudo(async () => {
      await setAdminUserStatus(uid, status);
      // On relit plutôt que de rapiécer la ligne : le changement d'état déplace
      // aussi les compteurs du tableau de bord, et deux vérités divergentes sur
      // le même écran sont pires qu'un aller-retour.
      await loadAdminConsole();
    });
  });
}
